import React, { useState, useEffect, useRef } from 'react'
import { Modal, Form, Button, Dropdown, Menu, Icon } from 'semantic-ui-react'
import { HelpDocsLink } from '../../../components/common/ExternalLink'
import Data from '../../../api/Data'

const DesignerDynamicTextPromptModal = ({ 
  handleSubmit, 
  setShowModal, 
  projectData,
  sectionType,
}) => {
  // States
  const [text, setText] = useState('')
  const [showSmartFieldsMenu, setShowSmartFieldsMenu] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, fromCursor: false })
  const [projectQuestions, setProjectQuestions] = useState([])
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
  const textInputRef = useRef(null)
  const inputWrapperRef = useRef(null)
  const modalContentRef = useRef(null)
  const smartFieldsMenuRef = useRef(null)

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!projectData || !projectData.id) return

      setIsLoadingQuestions(true)

      try {
        const { data, error } = await Data.getProjectQuestions(projectData.id, true)

        if (error) {
          console.error('Error fetching questions:', error)
          return
        }

        const questionData = Array.isArray(data) ? data : []
        const formattedQuestions = questionData.map((question) => {
          return {
            id: question.id,
            text: question.text,
            alias: question.alias || null,
            questionNumber: question.sortOrder + 1,
            datasetLinked: !!question.datasetId,
            datasetId: question.datasetId || null,
            columns: question.datasetColumns || []
          }
        })

        setProjectQuestions(formattedQuestions)
      } catch (err) {
        console.error('Error fetching project questions:', err)
      } finally {
        setIsLoadingQuestions(false)
      }
    }

    fetchQuestions()
  }, [projectData])

  const submitForm = (e) => {
    if (e && e.preventDefault) e.preventDefault()
    handleSubmit(e, { value: 'dynamic' }, text)
  }

  const insertSmartField = (value) => { 
    if (!value) return

    setText(currentText => {
      if (!textInputRef.current) return currentText
      
      const cursorPosition = textInputRef.current.selectionStart || 0
      let textBefore = currentText.substring(0, cursorPosition)
      const textAfter = currentText.substring(cursorPosition)

      if (menuPosition.fromCursor && textBefore.endsWith('{{')) {
        textBefore = textBefore.substring(0, textBefore.length - 2)
      }
      
      return textBefore + value + textAfter
    })

    setShowSmartFieldsMenu(false)
  }

  const handleTextChange = (e, { value }) => {
    setText(value)
  }

  const handleSmartFieldsButtonClick = () => {
    calculateMenuPosition()
    setShowSmartFieldsMenu(prev => !prev)
  }

  const calculateMenuPosition = (forCursor = false) => {
    try {
      if (!inputWrapperRef.current) return
      
      const inputRect = inputWrapperRef.current.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      
      if (forCursor && textInputRef.current) {
        const cursorPosition = textInputRef.current.selectionStart || 0
        const textBeforeCursor = text.substring(0, cursorPosition)

        const tempSpan = document.createElement('span')
        tempSpan.style.font = window.getComputedStyle(textInputRef.current).font
        tempSpan.style.whiteSpace = 'pre-wrap'
        tempSpan.textContent = textBeforeCursor
        document.body.appendChild(tempSpan)

        const textLines = textBeforeCursor.split('\n')
        const lineIndex = textLines.length - 1
        const lineWidth = tempSpan.offsetWidth % textInputRef.current.offsetWidth

        document.body.removeChild(tempSpan)

        const padding = parseInt(window.getComputedStyle(textInputRef.current).paddingLeft) || 0
        const lineHeight = parseInt(window.getComputedStyle(textInputRef.current).lineHeight) || 20

        const cursorLeft = lineWidth + padding
        const cursorTop = lineIndex * lineHeight + textInputRef.current.offsetTop + 20

        const menuWidth = 250
        const menuHeight = 200
        
        let top = cursorTop + lineHeight
        let left = cursorLeft

        if (top + menuHeight > windowHeight) {
          top = cursorTop - menuHeight
        }

        if (left + menuWidth > windowWidth) {
          left = cursorLeft - menuWidth
        }
        
        setMenuPosition({
          top,
          left,
          fromCursor: true
        })
      } else {
        setMenuPosition({
          top: inputRect.height + 5,
          left: 0,
          fromCursor: false
        })
      }
    } catch (error) {
      console.error("Error calculating menu position:", error)
      setMenuPosition({
        top: 0,
        left: 0,
        fromCursor: forCursor
      })
    }
  }

  const handleKeyDown = (e) => {
    // If user types {{, show smart fields menu
    if (e.key === '{') {
      const cursorPosition = e.target.selectionStart
      
      if (cursorPosition > 0 && e.target.value[cursorPosition - 1] === '{') {
        calculateMenuPosition(true)
        setShowSmartFieldsMenu(true)
      }
    }

    if (e.key === 'Escape') {
      setShowSmartFieldsMenu(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        smartFieldsMenuRef.current && 
        !smartFieldsMenuRef.current.contains(event.target) &&
        !inputWrapperRef.current?.contains(event.target)
      ) {
        setShowSmartFieldsMenu(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const renderSmartFieldsMenu = () => {
    const hardcodedFieldsVloggi = [
      { 
        text: 'Contributor name',
        value: '{{contributorName}}',
        key: 'contributorName'
      },
      { 
        text: 'Clip date & time',
        value: '{{date}}',
        key: 'date'
      },
      { 
        text: 'Location',
        value: '{{location}}',
        key: 'location'
      },
      { 
        text: 'Place name',
        value: '{{place}}',
        key: 'place'
      },
      { 
        text: 'Clip position',
        value: '{{clipListPosition}}',
        key: 'clipListPosition'
      }
    ]

    const hardcodedFieldsOpeningOrClosing = [
      { 
        text: 'Story name',
        value: '{{storyName}}',
        key: 'storyName'
      },
      { 
        text: 'Producer name',
        value: '{{producerName}}',
        key: 'producerName'
      },
      { 
        text: 'Contributor list',
        value: '{{contributorList}}',
        key: 'contributorList'
      }
    ]

    const isVloggi = sectionType === 'vloggi'
    const hardcodedFields = isVloggi ? hardcodedFieldsVloggi : hardcodedFieldsOpeningOrClosing   
    const hasMoreThan8Questions = projectQuestions.length > 8
    const multiplier = menuPosition.fromCursor ? 10 : 20
    const menuStyle = {
      position: 'absolute',
      top: hasMoreThan8Questions ?
        `calc(${menuPosition.top}px - ${projectQuestions.length * multiplier}px)` :
        `${menuPosition.top}px`,
      left: `${menuPosition.left}px`,
      zIndex: 1100,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
      width: '100%',
      minWidth: '250px',
      maxWidth: '300px',
    }

    const questionFields = projectQuestions.map((question) => {
      const questionNumber = question.questionNumber
      const label = question.alias || `Question ${questionNumber}`
      
      if (question.datasetLinked && question.columns && question.columns.length > 0) {
        return {
          text: label,
          key: `question-${questionNumber}`,
          disabled: true,
          isDropdown: true,
          childFields: question.columns.map(column => {
            const value = question.alias 
              ? `{{${question.alias}.${column}}}` 
              : `{{question${questionNumber}.${column}}}`
            return {
              text: column,
              key: `question-${questionNumber}-${column}`,
              value: value
            }
          })
        }
      } else {
        const value = question.alias 
          ? `{{${question.alias}.response}}` 
          : `{{question${questionNumber}.response}}`
        return {
          text: label,
          key: `question-${questionNumber}`,
          value: value
        }
      }
    })

    const allFields = isVloggi ? [...hardcodedFields, ...questionFields] : hardcodedFields
    
    return (
      <div ref={smartFieldsMenuRef} style={menuStyle}>
        <Menu vertical fluid style={{ border: 'none' }} size="small">
          {isLoadingQuestions ? (
            <Menu.Item disabled>
              <Icon name="spinner" loading />
              Loading smart fields…
            </Menu.Item>
          ) : (
            allFields.map(field => {
              if (field.isDropdown && field.childFields) {
                return (
                  <Dropdown item text={field.text} key={field.key}>
                    <Dropdown.Menu style={{
                      width: 'auto',
                      minWidth: '200px'
                    }}>
                      {field.childFields.map(childField => (
                        <Dropdown.Item 
                          key={childField.key}
                          text={childField.text}
                          onClick={(e) => {
                            e.stopPropagation()
                            insertSmartField(childField.value)
                          }}
                        />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )
              } else {
                return (
                  <Menu.Item
                    key={field.key}
                    disabled={field.disabled}
                    onClick={(e) => {
                      e.stopPropagation()
                      insertSmartField(field.value)
                    }}
                  >
                    {field.text}
                  </Menu.Item>
                )
              }
            })
          )}
        </Menu>
      </div>
    )
  }
  
  return (
    <Modal dimmer="blurring" open size="small" onClose={() => setShowModal(false)}>
      <Modal.Header>
        Dynamic Text
      </Modal.Header>
      <Modal.Content ref={modalContentRef}>
        <Modal.Description>
          <p>
            Use this to overlay personalised text that includes contributor-specific data.
            You can type freeform text and insert smart variables using the <code>{'{{ }}'}</code> format or by clicking the + button. Read more{' '}
            <HelpDocsLink documentTag="how-to-use-the-vloggi-video-template-builder-dynamic-static-and-visual-elements">
              here
            </HelpDocsLink>
            .
          </p>

          <Form onSubmit={submitForm}>
            <Form.Field>
              <div style={{ position: 'relative' }} ref={inputWrapperRef}>
                <div style={{ marginBottom: '8px' }}>
                  <Dropdown
                    icon='plus'
                    floating
                    button
                    className='icon'
                    style={{ 
                      backgroundColor: showSmartFieldsMenu ? '#6c4fff' : '#f0f0f0',
                      color: showSmartFieldsMenu ? 'white' : 'black',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      minWidth: '50px',
                      textAlign: 'center',
                      boxShadow: 'none'
                    }}
                    onClick={handleSmartFieldsButtonClick}
                  >
                    <Dropdown.Menu style={{ display: 'none' }}></Dropdown.Menu>
                  </Dropdown>
                </div>

                <textarea
                  ref={(input) => {
                    if (input) {
                      textInputRef.current = input
                    }
                  }}
                  style={{
                    height: '20px',
                    width: '100%',
                    resize: 'none',
                    borderRadius: '4px',
                    border: '1px solid rgba(34, 36, 38, 0.15)',
                    padding: '6px',
                    boxSizing: 'border-box',
                    fontSize: '14px',
                    minHeight: '6em',
                  }}
                  placeholder="Use smart fields by entering a '{{'"
                  value={text}
                  onChange={(e) => handleTextChange(e, { value: e.target.value })}
                  onKeyDown={handleKeyDown}
                />

                {showSmartFieldsMenu && renderSmartFieldsMenu()}
              </div>
            </Form.Field>
        </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setShowModal(false)}>Close</Button>
        <Button primary onClick={submitForm}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default DesignerDynamicTextPromptModal